// Lookup and search over the help articles. Pure, so it can be tested.
import { HELP_AREAS, HELP_ARTICLES } from "./articles";

/** "Hard sets per muscle" -> "hard-sets-per-muscle". */
export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// Help articles are meant to be taken in at a glance (#162): short steps, a
// screenshot per section, examples in small tables, and the "why" folded away
// under "More detail". A fold is written in the article as
//
//   +++ More detail
//   …markdown…
//   +++
//
// since react-markdown doesn't render raw <details>.
const FOLD = /^\+\+\+[ \t]*(.*)\n([\s\S]*?)\n\+\+\+[ \t]*$/m;

/** Article text as plain markdown and folds, in order. */
export const splitFolds = (content) => {
  const parts = [];
  let rest = String(content || "");
  for (let m = rest.match(FOLD); m; m = rest.match(FOLD)) {
    if (m.index > 0) parts.push({ text: rest.slice(0, m.index) });
    parts.push({ fold: m[1].trim() || "More detail", text: m[2] });
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest.trim()) parts.push({ text: rest });
  return parts;
};

const BY_ID = new Map(HELP_ARTICLES.map((a) => [a.id, a]));

export const getArticle = (id) => BY_ID.get(id) || null;

export const articlesInArea = (areaId) =>
  HELP_ARTICLES.filter((a) => a.area === areaId);

export const areaTitle = (areaId) =>
  HELP_AREAS.find((a) => a.id === areaId)?.title || "";

/**
 * An article split into its intro and `## ` sections:
 * { intro, sections: [{ id, title, body }] }.
 */
export const splitSections = (article) => {
  const lines = String(article?.body || "").trim().split("\n");
  const intro = [];
  const sections = [];
  let current = null;
  for (const line of lines) {
    const heading = line.match(/^##\s+(.*)$/);
    if (heading) {
      current = { id: slugify(heading[1]), title: heading[1].trim(), lines: [] };
      sections.push(current);
    } else if (current) {
      current.lines.push(line);
    } else {
      intro.push(line);
    }
  }
  return {
    intro: intro.join("\n").trim(),
    sections: sections.map(({ id, title, lines: body }) => ({
      id,
      title,
      body: body.join("\n").trim(),
    })),
  };
};

/** "article-id#section-id" -> { article, section } (section may be null). */
export const resolveTopic = (topic) => {
  const [articleId, sectionId] = String(topic || "").split("#");
  const article = getArticle(articleId);
  if (!article) return { article: null, section: null };
  const section = sectionId
    ? splitSections(article).sections.find((s) => s.id === sectionId) || null
    : null;
  return { article, section };
};

const plain = (markdown) =>
  String(markdown || "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // a screenshot reads as its alt text
    .replace(/^\+\+\+.*$/gm, " ") // "More detail" fold markers
    .replace(/[*_`>#|-]/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

/**
 * Articles matching every word of the query, best first: a title hit beats a
 * summary hit beats a body hit. Each result carries the best-matching section
 * so search can open straight to it.
 */
export const searchHelp = (query) => {
  const words = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 1);
  if (words.length === 0) return [];
  const results = [];
  for (const article of HELP_ARTICLES) {
    const title = article.title.toLowerCase();
    const summary = article.summary.toLowerCase();
    const { intro, sections } = splitSections(article);
    const all = `${title} ${summary} ${plain(intro)} ${sections
      .map((s) => `${s.title.toLowerCase()} ${plain(s.body)}`)
      .join(" ")}`;
    if (!words.every((w) => all.includes(w))) continue;
    let score = 0;
    for (const w of words) {
      if (title.includes(w)) score += 10;
      if (summary.includes(w)) score += 4;
    }
    let best = null;
    let bestScore = 0;
    for (const s of sections) {
      const text = `${s.title.toLowerCase()} ${plain(s.body)}`;
      const hit = words.reduce(
        (n, w) => n + (s.title.toLowerCase().includes(w) ? 3 : 0) + (text.includes(w) ? 1 : 0),
        0,
      );
      if (hit > bestScore) {
        bestScore = hit;
        best = s;
      }
    }
    results.push({ article, section: best, score: score + bestScore });
  }
  return results.sort((a, b) => b.score - a.score);
};
